'use client'

import { fetchAutosuggestAction } from '@/app/actions/autosuggest'
import Button from '@/app/components/common/Button'
import Popup from '@/app/components/common/Popup'
import FormInput from '@/app/components/Input'
import SearchAutosuggest from '@/app/components/SearchAutosuggest'
import { FeatureFlag } from '@/app/config/feature-flags'
import {
	AvailableInputTypes,
	COOKIE_NOTICE_STORAGE_KEY,
	DATE_FORMAT_QUERY,
	DEFAULT_ALLOCATION,
	DEFAULT_CURRENCY,
	defaultCheckInDate,
	defaultCheckOutDate,
	ENABLE_EMAIL_SIGNUP,
	FORM_TYPE,
	INIT_DATA_STORAGE_KEY,
	INPUT,
	LG_BREAKPOINT,
	MD_BREAKPOINT,
	PAGE_TYPE,
	REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY,
	REDIRECT_PARTNERS,
	ROOM_GUESTS_SEPARATOR,
	SM_BREAKPOINT,
	SM_HEIGHT_BREAKPOINT,
} from '@/app/constants'
import { useFeatureFlags } from '@/app/hooks/useFeatureFlags'
import {
	Allocation,
	SanityData,
	SearchItem,
	SearchItemLocationType,
	Suggestion,
	UserData,
} from '@/app/types'
import {
	capitalize,
	checkDate,
	getSearchCalendarMaxDate,
	getFormData,
	getGuestsCount,
	getLocaleByCode,
	getRedirectPartnerInfo,
	isSearchStayWithinApiLimits,
	parseGooglePlaceToSelectedItem,
	parseSearchParamsToLower,
	scrollToSelectedMonth,
	smoothScrollTo,
	submitHiddenForm,
	validateDateRange,
	validateEmail
} from '@/app/utils'
import { getCountryCode } from '@/app/utils/kayakRedirect'
import { useLocalStorage, useMediaQuery } from '@uidotdev/usehooks'
import { ApiMode } from 'autosuggest-sdk'
import classNames from 'classnames'
import { addDays, addWeeks, format, isValid } from 'date-fns'
import { camelCase, find, kebabCase, startCase } from 'lodash'
import { useTranslations } from 'next-intl'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { FC, FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import {
	SuggestionSelectedEventData,
	SuggestionsFetchRequestedParams,
} from 'react-autosuggest'

import { priceAlertSubscription } from '@/app/api/subscribe'
import BookingDatepicker from '@/app/components/BookingDatepicker'
import BookingPlaceholder from '@/app/components/BookingPlaceholder'
import Checkbox from '@/app/components/common/Checkbox'
import FullScreenDrawer from '@/app/components/common/FullScreenDrawer'
import EmailSignup from '@/app/components/EmailSignup'
import GuestsSelect from '@/app/components/GuestsSelect'
import { DisplayData } from '@/app/components/LocationDataHandler'
import SiteHeading from '@/app/components/SiteHeading'
import { Icons } from '@/app/components/ui/Icons'
import eventBus from '@/app/eventBus'
import {
	emitFormEmailSubmitGAEvent,
	emitFormEmailSubscribeBounceGAEvent,
	emitFormSearchGAEvent,
	emitFromDateGAEvent,
	emitGuestsClickGAEvent,
	emitGuestsSelectGAEvent,
	emitSearchWithoutLocationGAEvent,
	emitSignUpDealsGAEvent,
	emitToDateGAEvent,
} from '@/app/ga-event-helpers'
import { getKayakLanguageCode } from '@/app/languages'
import { PS_EVENTS } from '@/app/ps-events'
import { parseStayDateFromUrlOrLegacy } from '@/app/utils/calendar-date'
import { getLanguageCode } from '@/i18n/languageMapping'
import { captureMessage } from '@sentry/nextjs'
import dynamic from 'next/dynamic'
import { RemoveScroll } from 'react-remove-scroll'
import { buildRedirectUrl, isKayakMetaFlow, isNonAirportPlaceType } from '../utils/kayakRedirect'

export interface Props {
	displayValue?: DisplayData
	defaultLocation?: SearchItem | null
	isMainPage?: boolean
	isHotelLandingPage?: boolean
	sanityData?: SanityData
	loading?: boolean
	isHotelPage?: boolean
	customHeading?: boolean
	hotel?: any
	isExpanded?: boolean
	onClose?: () => void
}

const Booking: FC<Props> = ({
	displayValue,
	defaultLocation,
	isMainPage,
	sanityData,
	isHotelLandingPage,
	loading,
	onClose,
	hotel,
	isHotelPage,
	isExpanded,
	customHeading = false,
}) => {
	const router = useRouter()
	const searchParams = useSearchParams()
	const urlParams = useParams()
	const [_, setFrontFormData] = useLocalStorage(
		REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY
	)
	const [userData, setUserData] = useLocalStorage<UserData>(
		INIT_DATA_STORAGE_KEY
	)
	const [_cookie, setShowCookieNotice] = useLocalStorage(
		COOKIE_NOTICE_STORAGE_KEY
	)

	const getValidDate = (paramName: string, defaultDate: Date) => {
		const dateParam = searchParams.get(paramName)
		if (dateParam && checkDate(dateParam)) {
			const d = parseStayDateFromUrlOrLegacy(dateParam)
			return isValid(d) ? d : null
		}
		return null
	}

	const checkInDate = getValidDate('check-in-date', defaultCheckInDate)
	const checkOutDate = getValidDate('check-out-date', defaultCheckOutDate)

	const lowerCaseSearchParams = parseSearchParamsToLower(searchParams)
	const destCode = lowerCaseSearchParams?.destcode
	const preopenCalendar = lowerCaseSearchParams?.['cal']
	const shouldPreopenCalendar = preopenCalendar === '1'
	const roomsInputRef = useRef<HTMLDivElement>(null)
	const bookingRef = useRef<HTMLDivElement>(null)
	const redirectFormRef = useRef<HTMLFormElement>(null)
	const redirectFormBlankRef = useRef<HTMLFormElement>(null)
	const bookingInputsRef = useRef<HTMLDivElement>(null)
	const bookingInputDestRef = useRef<HTMLInputElement>(null)
	const bookingInputGuestsRef = useRef<HTMLInputElement>(null)
	const selectedItemRef = useRef<SearchItem | null>(null)
	const lastTypedQueryRef = useRef('')
	const lastFetchQueryRef = useRef('')
	const englishLocationNameRef = useRef<string | null>(null)
	// Debounce only the search_submission EMIT (never blocks the search itself),
	// plus an in-flight lock for the form_email_subscribe conversion (THP-101 A.3/A.4).
	const lastSearchSubmitRef = useRef(0)
	const emailSubscribeInFlightRef = useRef(false)
	// Snapshot of the rooms/guests allocation when the panel opens, so guests_select
	// only fires when the allocation actually changed (THP-101 B7).
	const guestsAtOpenRef = useRef<string>('')
	const isDesktop = useMediaQuery(LG_BREAKPOINT)
	const isMd = useMediaQuery(MD_BREAKPOINT)
	const isMobile = useMediaQuery(SM_BREAKPOINT)
	const smHeight = useMediaQuery(SM_HEIGHT_BREAKPOINT)
	const [suggestionsResult, setSuggestionsResult] = useState<SearchItem[]>([])
	const [suggestions, setSuggestions] = useState<Suggestion[]>([])
	const [selectedSuggestion, setSelectedSuggestion] = useState<string>()
	const [selectedItem, setSelectedItem] = useState<SearchItem | null>(null)
	const [searchValue, setSearchValue] = useState<DisplayData>()
	const [openedInput, setOpenedInput] = useState<AvailableInputTypes>(null)
	const t = useTranslations('Form')
	const locale = getLocaleByCode(urlParams.lang as string)
	const [dateFrom, setDateFrom] = useState<Date | null>(checkInDate || null)
	const [dateTo, setDateTo] = useState<Date | null>(checkOutDate || null)
	const [roomsAndGuests, setRoomsAndGuests] = useState<Allocation[]>([
		DEFAULT_ALLOCATION,
	])
	const [roomsAndGuestsText, setRoomsAndGuestsText] = useState<string>(
		t('roomsAndGuests', { roomsCount: 1, guestsCount: 2 })
	)
	const [alertChecked, setIsAlertChecked] = useState(false)
	const [previousDateFrom, setPreviousDateFrom] = useState<Date | null>(null)
	const [previousDateTo, setPreviousDateTo] = useState<Date | null>(null)
	const [isDatepickerOpen, setIsDatepickerOpen] = useState(false)
	const [datesAreChanged, setDatesAreChanged] = useState(false)
	const [inputError, setInputError] = useState<string | null>(null)
	const [isCompareChecked, setIsCompareChecked] = useState(true)
	const [userEmail, setUserEmail] = useState('')
	const [emailError, setEmailError] = useState<string>('')
	const [showNewHeading, setShowNewHeading] = useState(false)
	const [isOpenEmailSignup, setIsOpenEmailSignup] = useState(false)
	const { isEnabled: isFeatureEnabled } = useFeatureFlags()
	const {
		backTabPartner,
		frontTabPartner,
		hasBackTabRedirect,
		hasFrontPartner,
		isPartnerBooking,
	} = getRedirectPartnerInfo(searchParams)
	const shouldIgnoreFrontTab = frontTabPartner === '0'
	// const isFeatureOn = useFeatureIsOn('google_place')
	// const featureOnRef = useRef(false)

	const shouldShowEmailSignup: boolean = ENABLE_EMAIL_SIGNUP
	const shouldPerformSearch: boolean = !ENABLE_EMAIL_SIGNUP || !alertChecked

	useEffect(() => {
		const handleInputFocus = (inputType: AvailableInputTypes) => {
			setOpenedInput(inputType)

			if (inputType === INPUT.LOCATION && bookingInputDestRef.current) {
				setTimeout(() => bookingInputDestRef.current?.focus(), 0)
			} else if (inputType === INPUT.CALENDAR_FROM) {
				setTimeout(() => {
					openCalendar(INPUT.CALENDAR_FROM)
				}, 0)
			} else if (inputType === INPUT.GUESTS_SELECT && roomsInputRef.current) {
				setTimeout(() => roomsInputRef.current?.focus(), 0)
			}
		}

		const handleFieldError = () => {
			setTimeout(() => {
				setInputError(t('locationError'))
			}, 0)
		}

		const subscriptions = [
			eventBus.subscribe(PS_EVENTS.INPUT_FOCUS, handleInputFocus),
			eventBus.subscribe(PS_EVENTS.FIELD_ERROR, handleFieldError),
			eventBus.subscribe(PS_EVENTS.HANDLE_SEARCH, handleSearch),
		]

		return () => {
			subscriptions.forEach((unsub) => unsub())
		}
	}, [handleSearch])

	// useEffect(() => {
	// 	if (isFeatureOn) {
	// 		featureOnRef.current = true
	// 	}
	// }, [isFeatureOn])

	useEffect(() => {
		const timer = setTimeout(() => {
			setShowNewHeading(true)
		}, 600)

		return () => clearTimeout(timer)
	}, [])

	useEffect(() => {
		if (displayValue) {
			setSearchValue(displayValue)
		}
	}, [displayValue])

	useEffect(() => {
		if (defaultLocation) {
			setSelectedItem(defaultLocation)

			if (!selectedItemRef.current) {
				selectedItemRef.current = defaultLocation
			}
		}
	}, [defaultLocation])

	useEffect(() => {
		const checkInParam = searchParams.get('check-in-date')
		const checkOutParam = searchParams.get('check-out-date')
		const hadDateParams = !!(checkInParam || checkOutParam)

		const isCheckInValid = !!(checkInParam && checkDate(checkInParam))
		const isCheckOutValid = !!(checkOutParam && checkDate(checkOutParam))

		let needFallback = !isCheckInValid || !isCheckOutValid

		if (!needFallback && checkInParam && checkOutParam) {
			try {
				const inDate = parseStayDateFromUrlOrLegacy(checkInParam)
				const outDate = parseStayDateFromUrlOrLegacy(checkOutParam)
				if (isValid(inDate) && isValid(outDate) && inDate < outDate) {
					setDateFrom(inDate)
					setDateTo(outDate)
					setPreviousDateFrom(inDate)
					setPreviousDateTo(outDate)
				} else {
					needFallback = true
				}
			} catch {
				needFallback = true
			}
		}

		if (
			!needFallback &&
			checkInParam &&
			checkOutParam &&
			!isSearchStayWithinApiLimits(checkInParam, checkOutParam)
		) {
			needFallback = true
		}

		if (needFallback) {
			const isHotel =
				selectedItem?.loctype === SearchItemLocationType.HOTEL ||
				!!isHotelLandingPage

			const fallbackInDate = addWeeks(new Date(), 1)
			let fallbackOutDate = addWeeks(new Date(), 2)

			if (isHotel) {
				fallbackOutDate = addDays(fallbackInDate, 2)
			}

			setDateFrom(fallbackInDate)
			setDateTo(fallbackOutDate)
			setPreviousDateFrom(fallbackInDate)
			setPreviousDateTo(fallbackOutDate)

			const formattedIn = format(fallbackInDate, DATE_FORMAT_QUERY)
			const formattedOut = format(fallbackOutDate, DATE_FORMAT_QUERY)

			if (hadDateParams && (checkInParam !== formattedIn || checkOutParam !== formattedOut)) {
				updateURL(formattedIn, formattedOut)
			}
		}
	}, [searchParams, selectedItem?.loctype, isMainPage, isHotelLandingPage])

	const updateURL = useCallback(
		(newCheckIn: string, newCheckOut: string) => {
			const current = new URLSearchParams(Array.from(searchParams.entries()))
			current.set('check-in-date', newCheckIn)
			current.set('check-out-date', newCheckOut)

			const search = current.toString()
			const query = search ? `?${search}` : ''
			const path = typeof window !== 'undefined' ? window.location.pathname : ''
			router.replace(`${path}${query}`, { scroll: false })
		},
		[router, searchParams]
	)

	const calendarOpenedRef = useRef(false)

	useEffect(() => {
		//cal=1 logic should be ignored on single hotel page
		if (isHotelPage) return

		// Only attempt to open the calendar once when the component mounts
		if (shouldPreopenCalendar && !calendarOpenedRef.current) {
			calendarOpenedRef.current = true

			if (isMainPage) {
				openCalendar(INPUT.CALENDAR_FROM)
			} else {
				if (!isExpanded) {
					const inputType = isDesktop ? INPUT.CALENDAR_FROM : INPUT.MAIN_FORM
					eventBus.publish(PS_EVENTS.INPUT_FOCUS, inputType)
				} else if (isDesktop) {
					setTimeout(() => {
						openCalendar(INPUT.CALENDAR_FROM)
					}, 300)
				}
			}
		}
	}, [shouldPreopenCalendar, isMainPage, isExpanded, isHotelPage])

	useEffect(() => {
		eventBus.publish(PS_EVENTS.SELECTED_ITEM_CHANGED, {
			hasSelectedItem: !!selectedItem,
			selectedItem: selectedItem,
		})
		if (selectedItem) {
			let formData

			if (
				selectedItem.loctype === SearchItemLocationType.HOTEL ||
				(isHotelPage && hasBackTabRedirect && isPartnerBooking)
			) {
				const buid = searchParams.get('buid') || null

				formData = getFormData({
					...selectedItem,
					dateFrom,
					dateTo,
					roomsAndGuests,
					backTabPartner,
					buid,
					formType: FORM_TYPE.BACK,
					displayname: `${selectedItem.name}, ${selectedItem.cityonly}, ${selectedItem.country}`,
				})
			} else {
				const hasCityDestcode = destCode?.toLowerCase()?.includes('c:')
				const localDestcode = destCode?.includes(':')
					? destCode.substring(destCode.indexOf(':') + 1)
					: destCode

				formData = getFormData({
					...selectedItem,
					kayakType: 'city',
					...(selectedItem.ctid && { ptid: `${selectedItem.ctid}` }),
					displayname: selectedItem.cityname,
					dateFrom,
					dateTo,
					roomsAndGuests,
					formType: FORM_TYPE.FRONT,
					locationname: selectedItem.name,
					...(hasCityDestcode && { ptid: localDestcode }),
				})
			}

			setFrontFormData(formData)
		}
	}, [selectedItem, dateFrom, dateTo, roomsAndGuests])

	useEffect(() => {
		if (searchParams.has('room')) {
			let roomParam: string[] = searchParams.getAll('room')

			if (!Array.isArray(roomParam)) {
				roomParam = [roomParam]
			}

			const rooms = roomParam.map((room: string) => {
				const [adults, children = ''] = room.split(ROOM_GUESTS_SEPARATOR)
				const childAges = children
					? children.split(',').map((age) => Number(age))
					: []

				return {
					adults: parseInt(adults) || 1, // Default to 1 adult if parsing fails
					childAge: childAges,
				}
			})
			handleRoomsAndGuestsChange(rooms)
		}
	}, [searchParams])

	const getAutosuggestMode = useCallback(() => {
		const useOldApiParam = searchParams.get('use_old_api')
		if (useOldApiParam === 'true') {
			return 'deprecated'
		}
		if (useOldApiParam === 'false') {
			return 'new'
		}
		const newApiEnabled = isFeatureEnabled(
			FeatureFlag.NEW_AUTOCOMPLETE_API,
			urlParams.lang as string
		)
		return newApiEnabled ? 'new' : 'deprecated'
	}, [searchParams, isFeatureEnabled, urlParams.lang])

	const fetchSuggestions = useCallback(
		async (query: string) => {
			lastFetchQueryRef.current = query
			if (!query || query.trim().length === 0) {
				setSuggestions([])
				return
			}
			try {
				const mode = getAutosuggestMode() as ApiMode
				const language = urlParams.lang as string
				const countryCode = getCountryCode(language)
				const langCode = getKayakLanguageCode(language)
				const autosuggestLocale = mode === 'new' ? langCode : language
				const actionResult = await fetchAutosuggestAction({
					query,
					locale: autosuggestLocale,
					language: langCode,
					country: countryCode,
					mode,
				})
				if (!actionResult.success) {
					throw new Error(actionResult.error)
				}
				const response = actionResult.data!
				const result = response as SearchItem[]
				setSuggestionsResult(result)

				const suggestions = result.map((item) => {
					let itemType = item.loctype
					if (item.kayakType === 'hood' || item.kayakType === 'borough') {
						itemType = SearchItemLocationType.HOOD
					} else if (item.kayakType === 'island') {
						itemType = SearchItemLocationType.REGION
					}
					return {
						id: item.id,
						title:
							item.loctype === SearchItemLocationType.HOTEL
								? item.hotelname
								: item.displayname,
						type: itemType,
						image: item.destination_images?.image_webp,
						locationName: item.locationname,
						displayName: item.displayname,
						placeID: item.placeID,
						country: item.country,
						cityonly: item.cityonly,
					}
				})

				setSuggestions(suggestions)
			} catch (error: any) {
				console.error('Error fetching suggestions:', error)
				setSuggestions([])
			}
		},
		[urlParams.lang, getAutosuggestMode]
	)

	const onSuggestionsFetchRequested = (
		params: SuggestionsFetchRequestedParams
	) => {
		fetchSuggestions(params.value)
	}

	const onDateChange = (dates: Date[]) => {
		if (dates[0]?.getTime() === dates[1]?.getTime()) {
			dates[1] = addDays(dates[1], 1)
		}

		if (dates[0]) {
			setDateFrom(dates[0])
			setOpenedInput(INPUT.CALENDAR_TO)
			if (dates[0] && !dates[1]) {
				emitFromDateGAEvent()
			}
		}

		setDateTo(dates[1] ? dates[1] : null)

		if (dates[1]) {
			if (isDesktop) {
				handleClose()
				setIsDatepickerOpen(false)
				setDatesAreChanged(true)
			}
			emitToDateGAEvent()
		}
	}

	const openCalendar = (input: INPUT) => {
		setTimeout(() => {
			handleOpenInput(input)
		}, 100)

		setIsDatepickerOpen(true)
	}

	const handleCancelDateSelection = () => {
		setDateFrom(previousDateFrom)
		setDateTo(previousDateTo)
		setIsDatepickerOpen(false)
		handleClose()
	}

	const onSuggestionSelected = async (
		_event: FormEvent<any>,
		value: SuggestionSelectedEventData<Suggestion>
	) => {
		setInputError(null)
		const { id, placeID } = value.suggestion
		let selectedForLog: SearchItem | null = null

		if (id) {
			const selected = find(suggestionsResult, { id: id }) || null
			setSelectedItem(selected)
			selectedForLog = selected
		} else {
			try {
				const mappedPlace = await parseGooglePlaceToSelectedItem(placeID)
				setSelectedItem(mappedPlace)
				setSelectedSuggestion(mappedPlace.displayname)
				selectedForLog = mappedPlace
			} catch (error) {
				setInputError(t('locationError'))
				return
			}
		}
		englishLocationNameRef.current = null
		const sourceQuery = (lastFetchQueryRef.current || lastTypedQueryRef.current).trim()
		const isNonAsciiDisplayName = /[^\u0000-\u007F]/.test(String(selectedForLog?.displayname || ''))
		if (
			selectedForLog &&
			sourceQuery &&
			isNonAsciiDisplayName
		) {
			try {
				const countryForLookup = selectedForLog.cc || 'GB'
				const englishLookup = await fetchAutosuggestAction({
					query: sourceQuery,
					locale: 'en',
					language: 'en',
					country: countryForLookup,
					mode: 'new',
				})
				if (englishLookup.success && Array.isArray(englishLookup.data)) {
					const englishCandidates = englishLookup.data as SearchItem[]
					const matched = englishCandidates.find(
						(item) =>
							String(item.placeID || '') === String(selectedForLog?.placeID || '') ||
							String(item.ctid || '') === String(selectedForLog?.ctid || '')
					)
					if (matched?.displayname) {
						englishLocationNameRef.current = matched.displayname
					}
				}
			} catch (lookupError) {
			}
		}

		setSelectedSuggestion(value.suggestionValue)
		handleClose()

		if (!datesAreChanged) {
			setTimeout(() => {
				openCalendar(INPUT.CALENDAR_FROM)
			}, 200)
		}
	}

	const onInputChange = (value: string) => {
		setInputError(null)
		lastTypedQueryRef.current = value
		if (value !== selectedSuggestion) {
			// Removing the suggestion when input changes
			setSelectedItem(null)
		}
	}

	const handleRoomsAndGuestsChange = (rooms: Allocation[]) => {
		const roomsCount = rooms.length
		const guestsCount = getGuestsCount(rooms)

		setRoomsAndGuests(rooms)
		setRoomsAndGuestsText(t('roomsAndGuests', { roomsCount, guestsCount }))

		// handleClose()
	}

	const handleFreeRegion = (selectedItem: SearchItem, dest: string) => {
		const { name, smartyDisplay } = selectedItem

		if (selectedItem.country) {
			return `/${kebabCase(selectedItem.country)}/${kebabCase(name)}/`
		}
		return `${dest}/${kebabCase(smartyDisplay)}/${kebabCase(
			smartyDisplay
		)}/freereg/`
	}

	async function handleSearch() {
		// smoothScrollTo(0, 0)

		if (!selectedItem) {
			handleLocationError()
			return
		}

		const frontTabRedirectCriteria =
			(isMainPage || isHotelLandingPage || isHotelPage) && hasFrontPartner

		const current = new URLSearchParams(Array.from(searchParams.entries()))
		const existingParams = parseSearchParamsToLower(
			Object.fromEntries(current.entries())
		)

		const allowedPartners = searchParams.get('ap') || null
		const buid = searchParams.get('buid') || null
		const pt = searchParams.get('pt') || null
		const currency =
			searchParams.get('currency') ||
			userData?.selectedCurrency?.abbreviation ||
			userData?.defaultCurrency?.abbreviation ||
			DEFAULT_CURRENCY
		const userCountry = userData?.userCountry

		const {
			country,
			region,
			loctype,
			cityonly,
			name,
			smartyDisplay,
			kayakId: hotelID,
			placeID,
			airportname,
			timezone,
			ctid,
			cc: countryCode,
			googlePlaceId,
			lat,
			lng,
			utcOffsetMinutes,
		} = selectedItem

		const buildDestination = () => {
			let basePath = urlParams.lang ? `/${urlParams.lang}` : ''

			if (isHotelPage && selectedItem.id === selectedItemRef.current?.id) {
				basePath += '/hotel'
			}

			switch (loctype) {
				case SearchItemLocationType.AIRPORT:
					{
						const airportRegion = region || cityonly || name
						const airportDestination = `${basePath}/${kebabCase(country)}/${kebabCase(
							airportRegion || ''
						)}/ap/${encodeURIComponent(airportname || name)}`
						return airportDestination
					}
				case SearchItemLocationType.HOTEL:
					return `${basePath}/${kebabCase(country)}/${kebabCase(
						region || cityonly
					)}/${kebabCase(cityonly)}/${encodeURIComponent(name)}`
				case SearchItemLocationType.FREEREGION:
					return handleFreeRegion(selectedItem, basePath)
				case SearchItemLocationType.CITY:
					return `${basePath}/${kebabCase(country)}/${kebabCase(
						region || cityonly
					)}/${kebabCase(cityonly)}`
				case SearchItemLocationType.LM:
					return `${basePath}/${kebabCase(country)}/${kebabCase(
						region || cityonly
					)}/${kebabCase(cityonly)}/lm/${encodeURIComponent(name)}`
				// return `${basePath}/${kebabCase(country)}${
				// 	region ? `/${kebabCase(region)}` : ''
				// }${cityonly ? `/${kebabCase(cityonly)}` : ''}`
				case SearchItemLocationType.HOOD:
					return `${basePath}/${kebabCase(country)}/${kebabCase(
						region || cityonly
					)}/${kebabCase(cityonly)}/hood/${encodeURIComponent(name)}`
				default:
					return `${basePath}/${kebabCase(country)}${region ? `/${kebabCase(region)}` : ''
						}`
			}
		}

		const dest = buildDestination()

		// Validate dates before formatting
		if (!validateDateRange(dateFrom, dateTo) || !dateFrom || !dateTo) {
			console.error('Date validation failed')
			captureMessage('Date validation failed in handleSearch', {
				level: 'error',
				tags: {
					component: 'Booking Form',
					action: 'handleSearch',
					issue: 'date_validation_failed',
				},
				extra: {
					dateFrom: dateFrom,
					dateTo: dateTo || 'Not Selected',
					maxAllowedDate: getSearchCalendarMaxDate(),
					selectedItem: selectedItem
						? {
							id: selectedItem.id,
							name: selectedItem.name,
							loctype: selectedItem.loctype,
						}
						: null,
					userAgent: navigator.userAgent,
				},
			})
			return
		}

		const formattedDateFrom = format(dateFrom, DATE_FORMAT_QUERY)
		const formattedDateTo = format(dateTo, DATE_FORMAT_QUERY)

		const rooms = roomsAndGuests.map(
			(room) =>
				`${room.adults}|${room.childAge.map((age) => age.toString()).join(',')}`
		)

		const {
			loctype: paramLoctype,
			entitykey: paramEntityKey,
			slug,
			destcode: paramDestCode,
			locationname,
			placeid: paramPlaceID,
			hotelid: paramHotelID,
			usercountry: paramUserCountry,
			sessionid: paramSessionID,
			timezone: paramTimezone,
			f: frontParam,
			fuid,
			latlon: paramLatLon,
			utcoffsetminutes: paramUtcOffsetMinutes,
			googleplaceid: paramGooglePlaceID,
			page,
			mx: maxPriceParam,
			mn: minPriceParam,
			cal: openCalendarParam,
			...restParams
		} = existingParams || {}

		// This is to remove filters from the results page
		const filteredParams = isMainPage
			? restParams
			: Object.fromEntries(
				Object.entries(restParams).filter(
					([key]) =>
						!['s', 'h', 't', 'gr', 'by', 'dir', 'p', 'fa'].includes(key)
				)
			)

		const urlParamsObject = new URLSearchParams()
		urlParamsObject.set('currency', currency)
		urlParamsObject.set('check-in-date', formattedDateFrom)
		urlParamsObject.set('check-out-date', formattedDateTo)
		urlParamsObject.set('userCountry', userCountry)

		const cityName = selectedItem.cityonly && selectedItem.cityonly

		if (selectedItem.loctype === SearchItemLocationType.AIRPORT) {
			urlParamsObject.set('locationName', cityName || selectedItem.name)
		} else if (selectedItem.loctype === SearchItemLocationType.CITY) {
			urlParamsObject.set('locationName', cityName)
		} else if (selectedItem.loctype === SearchItemLocationType.HOTEL) {
			urlParamsObject.set('locationName', selectedItem.name)
		} else if (
			selectedItem.loctype === SearchItemLocationType.REGION ||
			selectedItem.loctype === SearchItemLocationType.COUNTRY ||
			selectedItem.loctype === SearchItemLocationType.FREEREGION
		) {
			urlParamsObject.set(
				'locationName',
				startCase(camelCase(selectedItem.name))
			)
		}

		// if (customEntityKey) {
		// 	urlParamsObject.set('entityKey', customEntityKey)
		// }
		if (userData?.sessionID) {
			urlParamsObject.set('sessionID', userData?.sessionID)
		}
		if (timezone) {
			urlParamsObject.set('timezone', timezone)
		}
		if (ctid) {
			urlParamsObject.set('ctid', ctid)
		}
		if (countryCode) {
			urlParamsObject.set('cc', countryCode)
		}
		if (placeID) {
			urlParamsObject.set('placeID', placeID)
		}

		if (
			selectedItem.loctype === SearchItemLocationType.HOTEL &&
			selectedItem.kayakId
		) {
			urlParamsObject.set('hotelID', hotelID)
		}
		if (allowedPartners) {
			urlParamsObject.set('ap', allowedPartners)
		}
		if (googlePlaceId) {
			urlParamsObject.set('googlePlaceId', googlePlaceId)
			urlParamsObject.set('latlon', `${lat},${lng}`)
			urlParamsObject.set('utcOffsetMinutes', String(utcOffsetMinutes))
		}

		rooms.forEach((room) => {
			urlParamsObject.append('room', room)
		})

		Object.entries(filteredParams).forEach(([key, value]) => {
			if (!urlParamsObject.has(key)) {
				urlParamsObject.set(key, value)
			}
		})

		const url = `${dest}?${urlParamsObject.toString()}`

		setShowCookieNotice(false)

		// Dedupe the search_submission analytics event on a fast double-fire
		// (double-click, or the handleSignupAndSearch `.finally(handleSearch)` path)
		// WITHOUT ever blocking the search: only the emit is debounced, the redirect
		// below always runs (THP-101 A.3).
		const nowTs = Date.now()
		if (nowTs - lastSearchSubmitRef.current > 1000) {
			lastSearchSubmitRef.current = nowTs
			emitFormSearchGAEvent({
				dateFrom: formattedDateFrom,
				dateTo: formattedDateTo,
				roomsAndGuests,
				value: smartyDisplay,
			})
		}

		const backTabRedirectCriteria =
			hasBackTabRedirect &&
			(isHotelLandingPage || isHotelPage || (isMainPage && isCompareChecked))

		const hasCityDestcode = destCode?.toLowerCase()?.includes('c:')
		const localDestcode = destCode?.includes(':')
			? destCode.substring(destCode.indexOf(':') + 1)
			: destCode

		const getCommonFormData = () => ({
			...selectedItem,
			dateFrom,
			dateTo,
			roomsAndGuests,
			backTabPartner,
			frontTabPartner,
			pt,
			buid,
			...(hasCityDestcode && { ptid: localDestcode }),
		})

		const handleRedirect = async (
			formType: FORM_TYPE,
			shouldOpenResults: boolean = false
		) => {
			let formData = getFormData({
				...getCommonFormData(),
				fuid,
				formType,
			})
			const initialDestinationId = formData['destination-id']
			// Deleting destination-id as it can be missing for some partners and is not needed for the redirect forms
			delete formData['destination-id'];

			// Kayak redirect: payload for backend. Debug approaches to try:
			// - Strip destination-id for city/region → backend may redirect to /stays (no deep link).
			// - Send destination-id C:Z42016 → backend builds slug that gives Kayak 404.
			// - Try other payload variants: destination-id format (Z42016, 42016?), location-name format (with/without commas).
			const isKayakFront = frontParam === REDIRECT_PARTNERS.KAYAK
			const isKayakBack = backTabPartner === REDIRECT_PARTNERS.KAYAK
			const isKayakFlow = (formType === FORM_TYPE.FRONT && isKayakFront) || (formType === FORM_TYPE.BACK && isKayakBack)
			const isVioFront = formType === FORM_TYPE.FRONT && frontParam === 'vio'
			const isVioBack = formType === FORM_TYPE.BACK && backTabPartner === 'vio'
			const isVioFlow = isVioFront || isVioBack
			const isExpediaPinnedFront = formType === FORM_TYPE.FRONT && frontParam === 'exp'
			const isExpediaPinnedBack = formType === FORM_TYPE.BACK && backTabPartner === 'exp'
			const isExpediaPinnedFlow = isExpediaPinnedFront || isExpediaPinnedBack
			const destKey = formData['destination-key']

			// Strip only when destKey is in STRIP_DEST_ID_DESTINATION_KEYS (kayakRedirect).
			const shouldStripIdsForKayak = isKayakFlow && isNonAirportPlaceType(destKey || '')

			if (shouldStripIdsForKayak) {
				formData = { ...formData }
				delete formData['destination-id']
				delete (formData as Record<string, unknown>).placeID
			}

			// Kayak hotels slug: city/region {Location}-p{placeID} (e.g. Capri,Naples,Italy-p43995); airport {Location}-p{placeID}-l{IATA} (e.g. Paris,France,Charles-de-Gaulle-p176414-lCDG).
			const isCityRegion = destKey === 'city' || destKey === 'region' || destKey === 'town'
			if (isKayakFlow && isCityRegion && selectedItem?.placeID) {
				formData = { ...formData, 'destination-id': `p${selectedItem.placeID}` }
			}
			if (isKayakFlow && destKey === 'airport' && selectedItem?.placeID && selectedItem?.ap) {
				formData = { ...formData, 'destination-id': `p${selectedItem.placeID}-l${String(selectedItem.ap).toUpperCase()}` }
			}

			// City destcode flow (destcode=C:{code}): Kayak backend needs C:{code} to resolve the city.
			// Overrides the p{placeID} above for this specific case.
			if (isKayakFlow && hasCityDestcode && localDestcode) {
				formData = { ...formData, 'destination-id': `C:${localDestcode}` }
			}

			// Kayak hotel deep link: backend needs H:{kayakId} (from getFormData / hotelID URL param).
			// Same for Expedia `exp` (MB-287): with H:{id} the backend pins the hotel, without it
			// falls back to a city search. `ex` stays city-search only, so it keeps the id stripped.
			if ((isKayakFlow || isExpediaPinnedFlow) && destKey === 'hotel' && initialDestinationId) {
				formData = { ...formData, 'destination-id': initialDestinationId }
			}

			const originalLocationName = String(formData['location-name'] || '')
			const hasNonAsciiLocationName = /[^\u0000-\u007F]/.test(originalLocationName)
			const shouldUseEnglishFallback = isVioFlow && String(urlParams.lang || '').toLowerCase() !== 'en'
			let englishLocationName = englishLocationNameRef.current
			if (shouldUseEnglishFallback && !englishLocationName && selectedItem) {
				const sourceQuery = (lastFetchQueryRef.current || lastTypedQueryRef.current || selectedItem.displayname || selectedItem.name || '').trim()
				const countryForLookup = selectedItem.cc || 'GB'
				if (sourceQuery) {
					try {
						const englishLookup = await fetchAutosuggestAction({
							query: sourceQuery,
							locale: 'en',
							language: 'en',
							country: countryForLookup,
							mode: 'new',
						})
						if (englishLookup.success && Array.isArray(englishLookup.data)) {
							const englishCandidates = englishLookup.data as SearchItem[]
							const matched = englishCandidates.find(
								(item) =>
									String(item.placeID || '') === String(selectedItem.placeID || '') ||
									String(item.ctid || '') === String(selectedItem.ctid || '')
							)
							englishLocationName = matched?.displayname || null
							if (englishLocationName) {
								englishLocationNameRef.current = englishLocationName
							}
						}
					} catch (lookupError) {
					}
				}
			}
			if (shouldUseEnglishFallback && englishLocationName) {
				formData = {
					...formData,
					'location-name': englishLocationName,
				}
			}

			if (formType === FORM_TYPE.BACK) {
				if (shouldOpenResults) {
					window.open(url, '_blank', 'noopener,noreferrer')
				}
				submitHiddenForm(formData, redirectFormRef.current as HTMLFormElement)
			} else if (formType === FORM_TYPE.FRONT) {
				const isKayakFlow = isKayakMetaFlow(frontParam, fuid)
				const redirectUrl = buildRedirectUrl(
					urlParams.lang as string,
					isKayakFlow,
					formData
				)
				// For both Kayak and non-Kayak flows with FORM_TYPE.FRONT: affiliate in new tab, current tab to results (like f=b behavior)
				window.open(redirectUrl, '_blank', 'noopener,noreferrer')
				if (shouldOpenResults) {
					router.push(url)
				}
				setFrontFormData(formData)
			}
		}

		// Determine if exactly one criteria is met
		const shouldOpenResultsPage =
			frontTabRedirectCriteria !== backTabRedirectCriteria

		if (frontTabRedirectCriteria) {
			await handleRedirect(FORM_TYPE.FRONT, shouldOpenResultsPage)
		}

		if (backTabRedirectCriteria) {
			const openFrontTabResults = shouldOpenResultsPage && !shouldIgnoreFrontTab
			await handleRedirect(FORM_TYPE.BACK, openFrontTabResults)
		}

		// Only redirect to results page if neither criteria is met
		if (!backTabRedirectCriteria && !frontTabRedirectCriteria) {
			router.push(url)
		}
	}

	const handleUserEmailChange = (value: string) => {
		setUserEmail(value)
		setEmailError('')
	}
	const handleLocationError = () => {
		onInputFocus()
		if (!isMobile) {
			setTimeout(() => {
				setInputError(t('locationError'))
			}, 100)
		}

		emitSearchWithoutLocationGAEvent()
	}

	const handleSignupAndSearch = (email: string) => {
		if (!selectedItem) {
			handleLocationError()
			return
		}

		if (userEmail.length) {
			const validEmail = validateEmail(userEmail)

			if (!validEmail) {
				setEmailError(t('emailError'))
				return
			}
		}

		if (!userEmail && !email) {
			emitFormEmailSubscribeBounceGAEvent()
			setIsOpenEmailSignup(false)
			handleSearch()
			return
		}

		// Skip subscription if feature is disabled
		if (!ENABLE_EMAIL_SIGNUP) {
			setUserData({ ...userData, email: userEmail || email })
			handleSearch()
			return
		}

		const buid = searchParams.get('buid') || null
		const fuid = searchParams.get('fuid') || null
		const pt = searchParams.get('pt') || null
		const hasCityDestcode = destCode?.toLowerCase()?.includes('c:')

		const formData = getFormData({
			...selectedItem,
			dateFrom,
			dateTo,
			roomsAndGuests,
			buid,
			fuid,
			pt,
			...(hasCityDestcode && { ptid: destCode }),
		})

		const {
			_adults,
			_children,
			'destination-id': _,
			...restFormData
		} = formData
		const { placeID } = selectedItem
		const currency =
			searchParams.get('currency') ||
			userData?.selectedCurrency?.abbreviation ||
			userData.defaultCurrency.abbreviation

		// In-flight lock: the conversion (form_email_subscribe) is emitted in the
		// .then() after the network round-trip; without a synchronous guard a
		// double-click fires priceAlertSubscription — and the conversion — twice
		// (THP-101 A.4). Cleared in .finally().
		if (emailSubscribeInFlightRef.current) return
		emailSubscribeInFlightRef.current = true

		priceAlertSubscription({
			...restFormData,
			email: userEmail || email,
			adults: formData._adults,
			children: formData._children,
			lng: getLanguageCode(urlParams.lang as string),
			currency: currency,
			'destination-id': placeID && placeID,
		})
			.then(() => {
				console.log('subscription successful')
				emitFormEmailSubmitGAEvent()
			})
			.catch((error) => {
				console.error('An error occurred during subscription:', error)
			})
			.finally(() => {
				emailSubscribeInFlightRef.current = false
				setUserData({ ...userData, email: userEmail || email })
				handleSearch()
			})
	}

	const handleOpenEmailDialog = () => {
		if (!selectedItem) {
			if (isMobile) {
			} else {
				onInputFocus()
				setInputError(t('locationError'))
			}

			return
		}
		setIsOpenEmailSignup(true)
	}

	const onCalendarClose = () => {
		if (dateFrom && dateTo === null) {
			const suggestedCheckout = addDays(dateFrom, 1)
			const maxDate = getSearchCalendarMaxDate()

			if (suggestedCheckout <= maxDate) {
				setDateTo(suggestedCheckout)
			}
		}

		setIsDatepickerOpen(false)
		handleClose()
	}

	const onRoomsAndGuestsOpen = () => {
		guestsAtOpenRef.current = JSON.stringify(roomsAndGuests)
		handleOpenInput(INPUT.GUESTS_SELECT)
		emitGuestsClickGAEvent()
	}

	const onRoomsAndGuestsClose = () => {
		if (!isDesktop) {
			smoothScrollTo(0, 0)
		}

		handleClose()

		// Only emit guests_select when the allocation actually changed since the
		// panel opened — closing an unchanged panel is a non-interaction (THP-101 B7).
		if (JSON.stringify(roomsAndGuests) === guestsAtOpenRef.current) {
			return
		}

		emitGuestsSelectGAEvent({
			rooms: roomsAndGuests.length,
			adults: roomsAndGuests.reduce((acc, room) => acc + room.adults, 0),
			children: roomsAndGuests.reduce(
				(acc, room) => acc + room.childAge.length,
				0
			),
			total: getGuestsCount(roomsAndGuests),
		})
	}

	const handleApplyGuestsAndSearch = () => {
		handleOpenInput(null)
		handleSearch()
	}

	useEffect(() => {
		if (isMobile && isDatepickerOpen) {
			setTimeout(() => scrollToSelectedMonth(dateFrom as Date), 0)
		}
	}, [isDatepickerOpen])

	const handleApplyDates = () => {
		setIsDatepickerOpen(false)
		setPreviousDateFrom(dateFrom)
		setPreviousDateTo(dateTo)
		eventBus.publish(PS_EVENTS.INPUT_FOCUS, INPUT.MAIN_FORM)
	}

	const handleOpenInput = (input: AvailableInputTypes) => {
		if (openedInput === INPUT.LOCATION && input === null) {
			smoothScrollTo(0, 0)
		}

		setInputError(null)
		setOpenedInput(input)

		if (smHeight && isDesktop) {
			bookingRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			})
		}
	}

	const handleClose = () => {
		setOpenedInput(null)
		if (!isDesktop) {
			eventBus.publish(PS_EVENTS.INPUT_FOCUS, INPUT.MAIN_FORM)
		}
		document.body.style.overflowY = 'initial'
	}

	const onInputFocus = (input: INPUT = INPUT.LOCATION) => {
		const isLocationInput = input === INPUT.LOCATION
		handleOpenInput(input)

		if (isLocationInput) {
			bookingInputDestRef.current?.focus()
		}

		if (!isDesktop) {
			document.body.style.overflowY = 'hidden'
		}
	}

	const handlePriceAlertClick = () => {
		setIsAlertChecked(!alertChecked)
		if (!alertChecked) {
			// opposite of checked, because it reads prev state
			emitSignUpDealsGAEvent()
		}
	}

	return (
		<div
			className={classNames('booking__form', {
				'pt-6 max-lg:border border-text-300 md:max-lg:mb-5 md:max-lg:mx-5':
					isMainPage,
			})}
		>
			{isMainPage && (
				<div className="text-center">
					<SiteHeading
						hotel={hotel}
						showNewHeading={showNewHeading}
						customHeading={customHeading}
						selectedItem={selectedItem}
						isDesktop={isDesktop}
						isLandingPage={isHotelLandingPage}
						sanityData={sanityData}
						isMd={isMd}
					/>
				</div>
			)}
			{/* Anchor (autoscroll) */}
			<div className="relative" style={{ top: '-110px' }} ref={bookingRef} />
			<div
				ref={bookingInputsRef}
				className={classNames(
					'booking w-full flex lg:mt-8 justify-center flex-wrap mx-auto',
					{ 'max-lg:px-3 xl:max-w-[1048px] lg:max-xl:container': isMainPage },
					{ 'max-w-[1048px]': !isMainPage }
				)}
			>
				<form
					ref={redirectFormRef}
					id="redirect-form"
					method="post"
					action={process.env.NEXT_PUBLIC_BACKEND_URL + '/hotels/redirect'}
				/>
				<form
					ref={redirectFormBlankRef}
					id="redirect-form_blank"
					method="post"
					target="_blank"
					action={process.env.NEXT_PUBLIC_BACKEND_URL + '/hotels/redirect'}
				/>
				<div className="booking__wrapper">
					<div
						className={classNames(
							'flex-1 w-full lg:h-full md:max-lg:mb-2 max-lg:mb-3',
							{
								'lg:my-0': isMainPage,
							}
						)}
					>
						<SearchAutosuggest
							ref={bookingInputDestRef}
							defaultValue={capitalize(
								searchValue?.displayValue || searchValue?.displayName || ''
							)}
							suggestions={suggestions}
							inputError={inputError}
							onSuggestionsFetchRequested={onSuggestionsFetchRequested}
							onSuggestionSelected={onSuggestionSelected}
							onInputChange={onInputChange}
							onInputFocus={onInputFocus}
							onInputBlur={handleClose}
							inputClassName={classNames('search-autosuggest__input', {
								'search-autosuggest__input--is-open new-autosuggest-input--open':
									openedInput === INPUT.LOCATION,
							})}
							lang={urlParams.lang as string}
							pageType={
								isMainPage
									? PAGE_TYPE.MAIN_PAGE
									: isHotelPage
										? PAGE_TYPE.HOTEL_PAGE
										: PAGE_TYPE.RESULTS_PAGE
							}
						/>
					</div>
					<BookingDatepicker
						dateFrom={dateFrom}
						dateTo={dateTo}
						isDatepickerOpen={isDatepickerOpen}
						openedInput={openedInput}
						isMobile={!isDesktop}
						isDesktop={isDesktop}
						locale={locale}
						onDateChange={onDateChange}
						onCalendarClose={onCalendarClose}
						openCalendar={openCalendar}
						handleCancelDateSelection={handleCancelDateSelection}
						handleApplyDates={handleApplyDates}
					/>

					<div ref={roomsInputRef} className="relative -top-2"></div>
					<div
						className={classNames(
							'booking__guests-input booking__additional-input',
							{
								'guests-open !border-text-700':
									openedInput === INPUT.GUESTS_SELECT,
							}
						)}
						id={INPUT.GUESTS_SELECT}
						data-test-id="GUESTS_SELECT"
					>
						<FormInput
							id={'rooms-and-guests-input'}
							onClick={onRoomsAndGuestsOpen}
							value={roomsAndGuestsText}
							className={classNames('outline-none max-lg:h-14 rounded-full', {
								'rooms-and-guests-input--is-open':
									openedInput === INPUT.GUESTS_SELECT,
							})}
							readonly
							ref={bookingInputGuestsRef}
						>
							<Icons.iconGuests />
						</FormInput>
						{!isDesktop ? (
							<RemoveScroll
								enabled={isMobile && openedInput === INPUT.GUESTS_SELECT}
								removeScrollBar={false}
							>
								<FullScreenDrawer
									isOpen={openedInput === INPUT.GUESTS_SELECT}
									onClose={onRoomsAndGuestsClose}
									headerText={t('roomsAndGuestsLabel')}
								>
									<GuestsSelect
										onApplyGuestsAndSearch={handleApplyGuestsAndSearch}
										onChange={handleRoomsAndGuestsChange}
										initialValue={roomsAndGuests}
										onClose={onRoomsAndGuestsClose}
										isMobile={true}
										isMainPage={isMainPage as boolean}
									/>
								</FullScreenDrawer>
							</RemoveScroll>
						) : (
							<Popup
								className="rounded-lg right-0 top-0 lg:top-[68px] z-[70]"
								isOpen={openedInput === INPUT.GUESTS_SELECT}
								onClose={onRoomsAndGuestsClose}
							>
								<GuestsSelect
									onApplyGuestsAndSearch={handleApplyGuestsAndSearch}
									onChange={handleRoomsAndGuestsChange}
									initialValue={roomsAndGuests}
									onClose={onRoomsAndGuestsClose}
									isMobile={false}
									isMainPage={isMainPage as boolean}
								/>
							</Popup>
						)}
					</div>
					<div
						data-test="search-button"
						className="hidden lg:block lg:order-2 xl:order-none"
					>
						<Button
							id="search-button"
							bold
							textColor="white"
							data-testid="search-button"
							onClick={
								shouldPerformSearch
									? handleSearch
									: handleOpenEmailDialog
							}
							name="Search"
							className={classNames(
								'!bg-gradient-to-r text-xl h-14 hover:opacity-80 from-main to-[#E02189] !rounded-full transition-all ease-linear w-fit flex items-center justify-center'
							)}
						>
							<Icons.iconLens className="xl:mr-3" />
							<span className="hidden xl:inline">
								{t('newDesign.searchButton')}
							</span>
						</Button>
					</div>
				</div>

				{!isDesktop && isMainPage && (
					<>
						<div className="lg:hidden justify-between w-full lg:px-2 mt-6">
							{hasBackTabRedirect && isMainPage && !isHotelLandingPage && (
								<Checkbox
									labelLeft={isPartnerBooking ? t('compareWith') : ''}
									labelRight={
										isPartnerBooking ? `Booking.com` : t('compareWithOthers')
									}
									checked={isCompareChecked}
									onChange={setIsCompareChecked}
									testId="compare-with-booking"
									variant="light"
								/>
							)}
						</div>
						{shouldShowEmailSignup && (
							<>
								<div className="price-alert-switch-mobile mb-6 mt-3 md:mt-0 w-full flex justify-start">
									<Checkbox
										labelRight={t('priceAlertSwitch')}
										checked={alertChecked}
										onChange={handlePriceAlertClick}
										testId="price-alert-switch"
										variant="main-dark"
									/>
								</div>
								{alertChecked && (
									// tablet & mobile input
									<EmailSignup
										destination={selectedItem as SearchItem}
										startDate={dateFrom as Date}
										endDate={dateTo as Date}
										isOpen={true}
										onChange={handleUserEmailChange}
										emailInputError={emailError}
										onSubmit={handleSignupAndSearch}
									/>
								)}
							</>
						)}
					</>
				)}
				<div
					data-test="search-button"
					className="block lg:hidden w-full lg:px-2 lg:order-3 xl:order-none"
				>
					<Button
						id="search-button"
						big
						bgColor="main"
						textColor="white"
						data-testid="search-button"
						onClick={
							shouldPerformSearch
								? handleSearch
								: () => handleSignupAndSearch(userEmail)
						}
						name="Search"
						className={classNames(
							'font-bold text-xl !bg-gradient-to-r h-14 from-main to-[#E02189] !rounded-full transition-all ease-linear flex items-center justify-center',
							{
								'mt-3': !isMainPage,
							}
						)}
					>
						<Icons.iconLens className="mr-3" />

						{shouldPerformSearch
							? isMobile && isMainPage && !isHotelLandingPage
								? t('newDesign.searchButtonMobile')
								: t('newDesign.searchButton')
							: t('signupAndSearch')}
					</Button>
				</div>
				<div
					className={classNames(
						'hidden lg:order-2 xl:order-none w-full xl:max-w-[1040px]',
						{
							hidden: !isMainPage,
							'lg:flex ': isMainPage,
						}
					)}
				>
					<div
						className={classNames(
							' flex items-center justify-between mt-5 w-full ps-2 lg:ps-5 pe-2 lg:pe-5 mb-4 lg:mb-0',
							{
								'lg:!justify-end':
									!hasBackTabRedirect && isMainPage && !isHotelLandingPage,
							}
						)}
					>
						{hasBackTabRedirect && isMainPage && !isHotelLandingPage && (
							<div className="compare-checkbox-desktop">
								<Checkbox
									labelLeft={isPartnerBooking ? t('compareWith') : ''}
									labelRight={
										isPartnerBooking ? `Booking.com` : t('compareWithOthers')
									}
									checked={isCompareChecked}
									onChange={setIsCompareChecked}
									testId="compare-with-booking"
									variant="white"
								/>
							</div>
						)}
						{isDesktop && shouldShowEmailSignup && (
							<div className="price-alert-switch">
								<Checkbox
									labelRight={t('priceAlertSwitch')}
									checked={alertChecked}
									onChange={handlePriceAlertClick}
									testId="price-alert-switch"
									variant="white"
								/>
							</div>
						)}
					</div>
				</div>
			</div>
			{isOpenEmailSignup &&
				selectedItem &&
				isDesktop &&
				shouldShowEmailSignup && (
					<EmailSignup
						destination={selectedItem as SearchItem}
						startDate={dateFrom as Date}
						endDate={dateTo as Date}
						isOpen={true}
						onSubmit={handleSignupAndSearch}
						guestCount={getGuestsCount(roomsAndGuests)}
					/>
				)}
		</div>
	)
}

export default dynamic(() => Promise.resolve(Booking), {
	ssr: false,
	suspense: true,
	loading: () => (
		<>
			<div className="home-page-placeholder">
				<BookingPlaceholder />
			</div>
			<div className="results-page-placeholder">{/* <TopLoading /> */}</div>
		</>
	),
})
